<template>
  <div class="cpa-form">
    <!-- Contract type: the FIRST choice — everything else depends on it -->
    <label class="cpa-form__label">{{ $t('affiliates.cpaTypeLabel') }}</label>
    <div class="cpa-form__types">
      <button type="button" class="cpa-type" :class="{ 'is-active': local.type === 'FLAT' }"
              :disabled="editing" @click="setType('FLAT')">
        <span class="cpa-type__icon cpa-type__icon--flat"><i class="fa-solid fa-money-bill-1"></i></span>
        <span class="cpa-type__body">
          <span class="cpa-type__title">{{ $t('affiliates.cpaTypeFlat') }}</span>
          <span class="cpa-type__desc">{{ $t('affiliates.cpaTypeFlatDesc') }}</span>
        </span>
      </button>
      <button type="button" class="cpa-type" :class="{ 'is-active': local.type === 'CRG' }"
              :disabled="editing" @click="setType('CRG')">
        <span class="cpa-type__icon cpa-type__icon--crg"><i class="fa-solid fa-percent"></i></span>
        <span class="cpa-type__body">
          <span class="cpa-type__title">{{ $t('affiliates.cpaTypeCrg') }}</span>
          <span class="cpa-type__desc">{{ $t('affiliates.cpaTypeCrgDesc') }}</span>
        </span>
      </button>
    </div>

    <template v-if="local.type">
      <b-row class="mt-1">
        <b-col cols="12" :lg="isCrg ? 6 : 12">
          <label>{{ $t('affiliates.costPerAcquisition') }} (CPA)</label>
          <b-form-input :value="local.cpa" type="number" min="0" @input="set('cpa', $event)"/>
        </b-col>
        <b-col v-if="isCrg" cols="12" lg="6">
          <label>{{ $t('affiliates.conversionRateGoals') }} (CRG %)</label>
          <b-form-input :value="local.crg" type="number" min="0" max="100" @input="set('crg', $event)"/>
        </b-col>
      </b-row>

      <!-- CRG rule: fixed 2-week period, FTD outside the window never pays -->
      <b-alert v-if="isCrg" show variant="info" class="mt-1 mb-50 p-75">
        <div><i class="fa-solid fa-calendar-week mr-25"></i><strong>{{ $t('affiliates.cpaPeriodFixed') }}</strong></div>
        <small>{{ $t('affiliates.cpaCrgRule') }}</small>
      </b-alert>

      <div class="cpa-form__note">
        <i class="fa-solid fa-bolt mr-25"></i>{{ $t('affiliates.cpaActivationNote') }}
      </div>

      <div v-if="namePreview" class="cpa-form__preview">
        <i class="fa-solid fa-signature mr-25"></i>
        {{ $t('affiliates.cpaNamePreview') }}: <strong>{{ namePreview }}</strong>
      </div>
    </template>
  </div>
</template>

<script>
/**
 * Shared CPA contract form (used by the CPA modal and the new-affiliate
 * wizard). No dates, no late-days, no contract dropdown — the backend
 * activates the contract on save, closes the previous one, and fixes the CRG
 * period at 2 weeks. FLAT hides every CRG field.
 */
// Strips " flat", legacy " crg+10" and the current " 1300+10" auto suffixes.
const SUFFIX_RE = /\s+(flat|crg\+\d+(\.\d+)?|\d+(\.\d+)?\+\d+(\.\d+)?)$/i

export default {
  name: 'AffiliateCpaForm',
  props: {
    value: { type: Object, default: () => ({ type: null, cpa: null, crg: null }) },
    // Base name used for the live "affiliate will be renamed to…" preview.
    affiliateName: { type: String, default: '' },
    editing: { type: Boolean, default: false },
  },
  data: () => ({ local: { type: null, cpa: null, crg: null } }),
  watch: {
    value: {
      immediate: true,
      handler(v) { this.local = { type: null, cpa: null, crg: null, ...(v || {}) } },
    },
  },
  computed: {
    isCrg() { return this.local.type === 'CRG' },
    namePreview() {
      const base = (this.affiliateName || '').trim().replace(SUFFIX_RE, '')
      if (!base || !this.local.type) return null
      if (this.local.type === 'FLAT') return `${base} flat`
      const cpa = this.local.cpa
      const crg = this.local.crg
      const price = cpa == null || cpa === '' ? '…' : Number(cpa)
      const pct = crg == null || crg === '' ? '…' : Number(crg)
      return `${base} ${price}+${pct}`
    },
  },
  methods: {
    setType(type) {
      if (this.editing) return
      const next = { ...this.local, type }
      if (type === 'FLAT') next.crg = null
      this.local = next
      this.$emit('input', next)
    },
    set(field, value) {
      const next = { ...this.local, [field]: value }
      this.local = next
      this.$emit('input', next)
    },
  },
}
</script>

<style scoped>
.cpa-form__label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6e6b7b;
  margin-bottom: 0.4rem;
  display: block;
}
.cpa-form__types {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem;
}
.cpa-type {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  text-align: left;
  border: 1.5px solid #d8d6de;
  border-radius: 12px;
  background: transparent;
  padding: 0.7rem 0.8rem;
  cursor: pointer;
  transition: all 0.15s ease;
}
.cpa-type:hover:not(:disabled) { border-color: #7367f0; }
.cpa-type.is-active {
  border-color: #7367f0;
  box-shadow: inset 0 0 0 1px #7367f0;
  background: rgba(115, 103, 240, 0.06);
}
.cpa-type:disabled { opacity: 0.6; cursor: not-allowed; }
.cpa-type__icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.95rem;
}
.cpa-type__icon--flat { background: rgba(40, 199, 111, 0.14); color: #28c76f; }
.cpa-type__icon--crg { background: rgba(115, 103, 240, 0.14); color: #7367f0; }
.cpa-type__body { display: flex; flex-direction: column; line-height: 1.25; min-width: 0; }
.cpa-type__title { font-size: 0.9rem; font-weight: 700; color: #5e5873; }
.cpa-type__desc { font-size: 0.72rem; color: #b9b9c3; }

.cpa-form__note {
  margin-top: 0.5rem;
  font-size: 0.78rem;
  color: #ff9f43;
  font-weight: 600;
}
.cpa-form__preview {
  margin-top: 0.4rem;
  padding: 0.45rem 0.7rem;
  border: 1px dashed rgba(115, 103, 240, 0.5);
  border-radius: 9px;
  background: rgba(115, 103, 240, 0.06);
  font-size: 0.82rem;
  color: #5e5873;
}

.dark-layout .cpa-type { border-color: #3b4253; }
.dark-layout .cpa-type.is-active { background: rgba(115, 103, 240, 0.12); }
.dark-layout .cpa-type__title { color: #d0d2d6; }
.dark-layout .cpa-form__preview { color: #d0d2d6; }

@media (max-width: 575px) {
  .cpa-form__types { grid-template-columns: 1fr; }
}
</style>
